import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, BehaviorSubject, filter, take, finalize } from 'rxjs';
import { SnackbarService } from '../services/snackbar.service';
import { IUserDataResponse } from '../../shared/models/auth/user';
import { AuthService } from '../services/auth.service';
import { AuthRepository } from '../auth/state/auth.repository';

// Добавляем переменные для отслеживания состояния обновления токена
const isRefreshing = new BehaviorSubject<boolean>(false);
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const authRepository = inject(AuthRepository);
  const snackbar = inject(SnackbarService);

  // Skip refresh token endpoint to avoid infinite loop
  if (req.url.includes('auth/refresh')) {
    return next(req);
  }

  const accessToken: string | null = getAccessToken();

  if (accessToken !== null) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Если уже идет обновление токена или это запрос на обновление токена
        if (req.url.includes('auth/refresh') || isRefreshing.value) {
          if (req.url.includes('auth/refresh')) {
            // Если это ошибка обновления токена - нужно разлогиниться
            snackbar.error('Сессия истекла. Пожалуйста, выполните вход повторно');
            authService.logout().subscribe(() => {
              router.navigate(['/public/login']);
            });
          }
          return throwError(() => error);
        }

        // Началось обновление токена
        isRefreshing.next(true);
        refreshTokenSubject.next(null);

        return authService.refreshToken().pipe(
          switchMap((newToken) => {
            isRefreshing.next(false);
            refreshTokenSubject.next(newToken.accessToken);
            
            // Retry the original request with new token
            const newReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken.accessToken}`,
              },
            });
            return next(newReq);
          }),
          catchError((refreshError) => {
            // Ошибка обновления - разлогиниваемся
            isRefreshing.next(false);
            refreshTokenSubject.next(null);
            snackbar.error('Сессия истекла. Пожалуйста, выполните вход повторно');
            
            authService.logout().subscribe(() => {
              router.navigate(['/public/login']);
            });
            
            return throwError(() => refreshError);
          }),
          finalize(() => {
            isRefreshing.next(false);
          })
        );
      }

      return throwError(() => {
        // Показываем ошибку только если это не ошибка авторизации
        if (error.status !== 401) {
          snackbar.error(error.error?.errors?.[0]?.message || 'Произошла неизвестная ошибка');
        }
        return error;
      });
    })
  );
};

function getAccessToken(): string | null {
  const storedData = localStorage.getItem('auth');
  if (storedData) {
    const userData: IUserDataResponse = JSON.parse(storedData);
    if (userData.token !== null) {
      return userData.token.accessToken;
    }
    return null;
  }

  return null;
}
